/**
 * Fitmar Shopify Theme - Main JavaScript
 * Progressive enhancement only - core shopping works without JS
 */

import {
  formatFallbackMoneyFromMinorUnits,
  formatMoneyFromMinorUnits,
  routePath,
  themeString,
} from './theme-context.js';

const formatStoreMoneyFromMinorUnits = (minorUnits) =>
  formatMoneyFromMinorUnits(minorUnits) ?? formatFallbackMoneyFromMinorUnits(minorUnits);

// Lazy load modules (cached to prevent double-execution)
const _moduleCache = new Map();
const loadModule = async (name) => {
  if (_moduleCache.has(name)) return _moduleCache.get(name);
  const promise = import(`./${name}.js`)
    .then(m => m.default || m)
    .catch(e => { console.warn(`Failed to load module: ${name}`, e); _moduleCache.delete(name); return null; });
  _moduleCache.set(name, promise);
  return promise;
};

const getFocusableElements = (container) => Array.from(container?.querySelectorAll(
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), details summary, [tabindex]:not([tabindex="-1"])'
) || []).filter((element) => {
  if (element.hasAttribute('hidden') || element.getAttribute('aria-hidden') === 'true') return false;
  return element.offsetParent !== null || element === document.activeElement;
});

const trapFocus = (event, container, fallback = container) => {
  if (event.key !== 'Tab') return;

  const focusableElements = getFocusableElements(container);
  if (!focusableElements.length) {
    event.preventDefault();
    fallback?.focus?.({ preventScroll: true });
    return;
  }

  const firstElement = focusableElements[0];
  const lastElement = focusableElements[focusableElements.length - 1];

  if (event.shiftKey && document.activeElement === firstElement) {
    event.preventDefault();
    lastElement.focus();
  } else if (!event.shiftKey && document.activeElement === lastElement) {
    event.preventDefault();
    firstElement.focus();
  }
};

const initFormPendingStates = () => {
  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || form.dataset.skipPendingState === 'true') return;
    if (event.defaultPrevented) return;
    if (typeof form.checkValidity === 'function' && !form.checkValidity()) return;

    form.setAttribute('aria-busy', 'true');
    form.querySelectorAll('button[type="submit"], input[type="submit"]').forEach((submit) => {
      submit.setAttribute('aria-disabled', 'true');
    });
  });

  window.addEventListener('pageshow', () => {
    document.querySelectorAll('form[aria-busy="true"]').forEach((form) => {
      form.removeAttribute('aria-busy');
      form.querySelectorAll('[aria-disabled="true"]').forEach((submit) => {
        submit.removeAttribute('aria-disabled');
      });
    });
  });
};

// Initialize on DOM ready
const init = () => {
  // Form pending states
  initFormPendingStates();

  // Mobile drawer (menu + search)
  initMobileDrawer();

  // Quantity buttons
  initQuantityButtons();

  // Wishlist functionality
  initWishlist();

  // Filter drawer (mobile)
  initFilterDrawer();

  // Dropdown navigation
  initDropdownNav();

  // Country selector modal
  initCountrySelector();

  // Footer money-back guarantee modal
  initFooterGuaranteeModal();

  // Smart sticky header (category nav hide/show on scroll)
  initSmartHeader();

  // Lazy load page-specific modules
  if (document.querySelector('[data-product-form]')) {
    loadModule('product-form');
  }

  if (document.querySelector('[data-cart-form]') || document.querySelector('[data-cart-action]') || document.querySelector('[data-add-to-cart]') || document.querySelector('[data-recently-viewed]')) {
    loadModule('cart');
  }

  if (document.querySelector('[data-product-tabs]')) {
    loadModule('tabs');
  }

  if (document.querySelector('[data-search]')) {
    loadModule('search');
  }

  // Checkout upsell modal (intercepts checkout clicks globally)
  if (document.getElementById('fm-upsell-config')) {
    loadModule('checkout-upsell');
  }
};

/**
 * Mobile Drawer (Menu + Search)
 *
 * State machine:
 *   CLOSED → (hamburger)    → DRAWER_MENU
 *   CLOSED → (search icon)  → DRAWER_SEARCH
 *   MENU   → (input focus)  → DRAWER_SEARCH
 *   SEARCH → (close/Escape) → DRAWER_MENU
 *   MENU   → (close/Escape/backdrop) → CLOSED
 *   SEARCH → (backdrop)     → CLOSED
 */
const initMobileDrawer = () => {
  const drawer = document.getElementById('mobile-drawer');
  const backdrop = document.querySelector('[data-drawer-backdrop]');
  const menuToggle = document.querySelector('[data-drawer-toggle]');
  const searchToggle = document.querySelector('[data-drawer-search-toggle]');
  const closeBtn = document.querySelector('[data-drawer-close]');
  const searchInput = document.querySelector('[data-drawer-search-input]');
  const menuPanel = document.querySelector('[data-drawer-menu]');
  const searchContent = document.querySelector('[data-drawer-search-content]');

  if (!drawer) return;

  let isOpen = false;
  let isSearchActive = false;
  let activeDrawerTrigger = null;
  let previousBodyOverflow = '';

  const openDrawer = (activateSearch = false, trigger = null) => {
    if (isOpen) {
      if (activateSearch && !isSearchActive) activateSearchMode();
      return;
    }
    activeDrawerTrigger = trigger || document.activeElement;
    previousBodyOverflow = document.body.style.overflow;
    isOpen = true;
    drawer.hidden = false;
    drawer.inert = false;
    drawer.setAttribute('aria-hidden', 'false');
    // Force reflow so transition plays
    drawer.offsetHeight;
    drawer.classList.add('is-open');
    backdrop?.classList.add('is-visible');
    document.body.style.overflow = 'hidden';
    menuToggle?.setAttribute('aria-expanded', 'true');
    searchToggle?.setAttribute('aria-expanded', 'true');

    if (activateSearch) {
      activateSearchMode();
    } else {
      closeBtn?.focus({ preventScroll: true });
    }
  };

  const closeDrawer = () => {
    if (!isOpen) return;
    isOpen = false;
    isSearchActive = false;
    drawer.classList.remove('is-open', 'is-search-active');
    drawer.inert = true;
    drawer.setAttribute('aria-hidden', 'true');
    backdrop?.classList.remove('is-visible');
    document.body.style.overflow = previousBodyOverflow;
    menuToggle?.setAttribute('aria-expanded', 'false');
    searchToggle?.setAttribute('aria-expanded', 'false');

    // Hide search content, show menu
    if (searchContent) searchContent.hidden = true;
    if (menuPanel) menuPanel.style.display = '';

    // Clear search input
    if (searchInput) searchInput.value = '';

    // Hide after transition; timeout covers reduced-motion/no-transition environments.
    const hideClosedDrawer = () => {
      if (!isOpen) drawer.hidden = true;
    };
    drawer.addEventListener('transitionend', hideClosedDrawer, { once: true });
    window.setTimeout(hideClosedDrawer, 350);

    if (activeDrawerTrigger?.isConnected) {
      activeDrawerTrigger.focus({ preventScroll: true });
    }
    activeDrawerTrigger = null;
  };

  const activateSearchMode = () => {
    if (isSearchActive) return;
    isSearchActive = true;
    drawer.classList.add('is-search-active');
    if (searchContent) searchContent.hidden = false;
    if (menuPanel) menuPanel.style.display = 'none';
    searchInput?.focus();
    loadSearchSuggestions();
  };

  const deactivateSearchMode = () => {
    if (!isSearchActive) return;
    isSearchActive = false;
    drawer.classList.remove('is-search-active');
    if (searchContent) searchContent.hidden = true;
    if (menuPanel) menuPanel.style.display = '';
    if (searchInput) searchInput.value = '';
    // Hide live results, show suggestions
    const liveResults = document.querySelector('[data-drawer-search-results]');
    if (liveResults) liveResults.hidden = true;
  };

  // Hamburger opens drawer in menu mode
  menuToggle?.addEventListener('click', (event) => openDrawer(false, event.currentTarget));

  // Search icon opens drawer in search mode
  searchToggle?.addEventListener('click', (event) => openDrawer(true, event.currentTarget));

  // Close button: if search active → go to menu, else close drawer
  closeBtn?.addEventListener('click', () => {
    if (isSearchActive) {
      deactivateSearchMode();
    } else {
      closeDrawer();
    }
  });

  // Backdrop always closes fully
  backdrop?.addEventListener('click', closeDrawer);

  // Escape key: same logic as close button
  document.addEventListener('keydown', (e) => {
    if (!isOpen) return;

    if (e.key === 'Escape') {
      if (isSearchActive) {
        deactivateSearchMode();
      } else {
        closeDrawer();
      }
      return;
    }

    trapFocus(e, drawer, closeBtn || drawer);
  });

  // Focus on search input activates search mode
  searchInput?.addEventListener('focus', () => {
    if (isOpen && !isSearchActive) {
      activateSearchMode();
    }
  });

  // Trending / recent term click → fill input + trigger search
  document.addEventListener('click', (e) => {
    const tag = e.target.closest('[data-search-term]');
    if (!tag || !drawer.contains(tag)) return;
    const term = tag.dataset.searchTerm;
    if (searchInput && term) {
      searchInput.value = term;
      searchInput.dispatchEvent(new Event('input', { bubbles: true }));
      searchInput.focus();
    }
  });

  // Clear recent searches
  document.addEventListener('click', (e) => {
    if (!e.target.closest('[data-clear-recent-searches]')) return;
    try { localStorage.removeItem('fitmar_recent_searches'); } catch {}
    const section = document.querySelector('[data-recently-searched-section]');
    if (section) section.hidden = true;
  });
};

/**
 * Load search suggestions into the mobile drawer
 * (recently searched terms, recently viewed products)
 */
const loadSearchSuggestions = () => {
  // Recently searched
  const recentSection = document.querySelector('[data-recently-searched-section]');
  const recentList = document.querySelector('[data-recently-searched-list]');
  if (recentSection && recentList) {
    try {
      const terms = JSON.parse(localStorage.getItem('fitmar_recent_searches')) || [];
      if (terms.length > 0) {
        recentList.replaceChildren();
        terms.forEach((term) => {
          const li = document.createElement('li');
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'mobile-drawer__search-tag';
          btn.dataset.searchTerm = term;
          btn.textContent = term;
          li.appendChild(btn);
          recentList.appendChild(li);
        });
        recentSection.hidden = false;
      } else {
        recentSection.hidden = true;
      }
    } catch {
      recentSection.hidden = true;
    }
  }

  // Recently viewed products
  const rvSection = document.querySelector('[data-drawer-rv-section]');
  const rvGrid = document.querySelector('[data-drawer-rv-grid]');
  if (rvSection && rvGrid) {
    try {
      const handles = JSON.parse(localStorage.getItem('fitmar_recently_viewed')) || [];
      if (handles.length > 0) {
        const displayHandles = handles.slice(0, 5);
        rvGrid.replaceChildren();
        rvSection.hidden = false;

        const svgNS = 'http://www.w3.org/2000/svg';
        const makeSvgIcon = (d) => {
          const svg = document.createElementNS(svgNS, 'svg');
          svg.setAttribute('fill', 'none');
          svg.setAttribute('viewBox', '0 0 24 24');
          svg.setAttribute('stroke', 'currentColor');
          svg.setAttribute('stroke-width', '2');
          svg.setAttribute('aria-hidden', 'true');
          const p = document.createElementNS(svgNS, 'path');
          p.setAttribute('stroke-linecap', 'round');
          p.setAttribute('stroke-linejoin', 'round');
          p.setAttribute('d', d);
          svg.appendChild(p);
          return svg;
        };

        Promise.allSettled(
          displayHandles.map((handle) =>
            fetch(routePath(`products/${handle}.js`)).then((r) => r.ok ? r.json() : null)
          )
        ).then((results) => {
          results.forEach((result, i) => {
            if (result.status !== 'fulfilled' || !result.value) return;
            const product = result.value;
            const handle = displayHandles[i];
            const cheapest = [...product.variants].sort((a, b) => a.price - b.price).find((v) => v.available);
            const variant = cheapest || product.variants[0];
            const rawImg = typeof product.featured_image === 'string' ? product.featured_image : (product.image?.src || '');
            const imgUrl = rawImg ? rawImg.replace(/\.([^.]+)$/, '_200x.$1') : '';

            const wrapper = document.createElement('div');
            wrapper.className = 'mobile-drawer__rv-item';

            const link = document.createElement('a');
            link.href = routePath(`products/${handle}`);
            link.className = 'mobile-drawer__rv-link';

            if (imgUrl) {
              const img = document.createElement('img');
              img.src = imgUrl;
              img.alt = product.title;
              img.className = 'mobile-drawer__rv-image';
              img.width = 50;
              img.height = 50;
              img.loading = 'lazy';
              link.appendChild(img);
            }

            const info = document.createElement('div');
            info.className = 'mobile-drawer__rv-info';

            const title = document.createElement('span');
            title.className = 'mobile-drawer__rv-title';
            title.textContent = product.title;
            info.appendChild(title);

            const priceEl = document.createElement('span');
            priceEl.className = 'mobile-drawer__rv-price';
            priceEl.textContent = formatStoreMoneyFromMinorUnits(variant.price);
            info.appendChild(priceEl);

            link.appendChild(info);
            wrapper.appendChild(link);

            if (cheapest) {
              const cartAction = document.createElement('div');
              cartAction.className = 'search-item__cart-action';
              cartAction.dataset.cartAction = '';
              cartAction.dataset.variantId = cheapest.id;
              cartAction.dataset.productTitle = product.title;
              cartAction.dataset.variantTitle = cheapest.title;

              // Add-to-cart button
              const addBtn = document.createElement('button');
              addBtn.type = 'button';
              addBtn.className = 'search-item__add-btn';
              addBtn.dataset.addToCart = '';
              addBtn.setAttribute('aria-label', `${themeString('products.addToCart')}: ${product.title}`);
              const addText = document.createElement('span');
              addText.className = 'search-item__add-text';
              addText.textContent = themeString('products.addToCart');
              addBtn.appendChild(addText);
              const cartSvg = document.createElementNS(svgNS, 'svg');
              cartSvg.setAttribute('class', 'search-item__add-icon');
              cartSvg.setAttribute('width', '16');
              cartSvg.setAttribute('height', '16');
              cartSvg.setAttribute('viewBox', '0 0 24 24');
              cartSvg.setAttribute('fill', 'none');
              cartSvg.setAttribute('stroke', 'currentColor');
              cartSvg.setAttribute('stroke-width', '2');
              cartSvg.setAttribute('stroke-linecap', 'round');
              cartSvg.setAttribute('stroke-linejoin', 'round');
              const c1 = document.createElementNS(svgNS, 'circle');
              c1.setAttribute('cx', '9'); c1.setAttribute('cy', '21'); c1.setAttribute('r', '1');
              const c2 = document.createElementNS(svgNS, 'circle');
              c2.setAttribute('cx', '20'); c2.setAttribute('cy', '21'); c2.setAttribute('r', '1');
              const cartPath = document.createElementNS(svgNS, 'path');
              cartPath.setAttribute('d', 'M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6');
              cartSvg.append(c1, c2, cartPath);
              addBtn.appendChild(cartSvg);
              cartAction.appendChild(addBtn);

              // Stepper (shown when qty > 0)
              const stepper = document.createElement('div');
              stepper.className = 'search-item__stepper';
              stepper.dataset.stepper = '';
              stepper.hidden = true;
              const minusBtn = document.createElement('button');
              minusBtn.type = 'button';
              minusBtn.className = 'search-item__stepper-btn search-item__stepper-btn--minus';
              minusBtn.dataset.stepperMinus = '';
              minusBtn.setAttribute('aria-label', `${themeString('products.removeOne')}: ${product.title}`);
              minusBtn.appendChild(makeSvgIcon('M20 12H4'));
              const qtySpan = document.createElement('span');
              qtySpan.className = 'search-item__stepper-qty';
              qtySpan.dataset.stepperQty = '';
              qtySpan.setAttribute('tabindex', '0');
              qtySpan.setAttribute('role', 'spinbutton');
              qtySpan.setAttribute('aria-valuenow', '0');
              qtySpan.setAttribute('aria-valuemin', '0');
              qtySpan.setAttribute('aria-label', `${themeString('products.quantity')}: ${product.title}`);
              qtySpan.setAttribute('aria-live', 'polite');
              qtySpan.textContent = '0';
              const plusBtn = document.createElement('button');
              plusBtn.type = 'button';
              plusBtn.className = 'search-item__stepper-btn search-item__stepper-btn--plus';
              plusBtn.dataset.stepperPlus = '';
              plusBtn.setAttribute('aria-label', `${themeString('products.addOne')}: ${product.title}`);
              plusBtn.appendChild(makeSvgIcon('M12 4v16m8-8H4'));
              stepper.append(minusBtn, qtySpan, plusBtn);
              cartAction.appendChild(stepper);
              wrapper.appendChild(cartAction);
            }

            rvGrid.appendChild(wrapper);
          });

          loadModule('cart').then(() => document.dispatchEvent(new CustomEvent('cart:changed')));
        });
      } else {
        rvSection.hidden = true;
      }
    } catch {
      rvSection.hidden = true;
    }
  }
};

/**
 * Quantity Buttons (+/-)
 * Note: Cart page has its own handler in cart.js, skip if inside cart form
 */
const initQuantityButtons = () => {
  document.addEventListener('click', (e) => {
    const minus = e.target.closest('[data-quantity-minus]');
    const plus = e.target.closest('[data-quantity-plus]');

    if (!minus && !plus) return;

    // Skip if this is inside a cart form (cart.js handles it)
    if ((minus || plus).closest('[data-cart-form]')) return;

    const container = (minus || plus).closest('.quantity');
    const input = container?.querySelector('[data-quantity-input]');

    if (!input) return;

    const currentValue = parseInt(input.value, 10) || 1;
    const min = parseInt(input.min, 10) || 0;
    const max = parseInt(input.max, 10) || Infinity;

    if (minus && currentValue > min) {
      input.value = currentValue - 1;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    if (plus && currentValue < max) {
      input.value = currentValue + 1;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  });
};

/**
 * Wishlist Functionality (localStorage based)
 */
const initWishlist = () => {
  const STORAGE_KEY = 'fitmar_wishlist';

  const getWishlist = () => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
    } catch {
      return [];
    }
  };

  const saveWishlist = (items) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  };

  const toggleWishlist = (productId) => {
    const wishlist = getWishlist();
    const index = wishlist.indexOf(productId);

    if (index > -1) {
      wishlist.splice(index, 1);
    } else {
      wishlist.push(productId);
    }

    saveWishlist(wishlist);
    updateWishlistUI();
    return index === -1; // Returns true if added, false if removed
  };

  const isInWishlist = (productId) => {
    return getWishlist().includes(productId);
  };

  const updateWishlistUI = () => {
    const wishlist = getWishlist();
    const countEl = document.querySelector('[data-wishlist-count]');

    // Update count badge
    if (countEl) {
      countEl.textContent = wishlist.length;
      countEl.hidden = wishlist.length === 0;
    }

    // Update button states
    document.querySelectorAll('[data-wishlist-add]').forEach((btn) => {
      const productId = btn.dataset.wishlistAdd;
      btn.classList.toggle('is-active', isInWishlist(productId));
    });

    // Update wishlist page count if present
    const pageCountEl = document.querySelector('[data-wishlist-page-count]');
    const pageCountNumber = document.querySelector('[data-wishlist-page-count-number]');
    const pageCountText = document.querySelector('[data-wishlist-page-count-text]');
    if (pageCountEl && pageCountNumber) {
      pageCountNumber.textContent = wishlist.length;
      pageCountEl.hidden = wishlist.length === 0;
      if (pageCountText) {
        pageCountText.textContent = getWishlistProductPlural(wishlist.length);
      }
    }
  };

  const getWishlistProductPlural = (count) => {
    if (count === 1) return themeString('wishlist.itemSingular', 'product');
    if (count >= 2 && count <= 4) return themeString('wishlist.itemFew', themeString('wishlist.itemMany', 'products'));
    return themeString('wishlist.itemMany', 'products');
  };

  // Toast notification for wishlist actions
  const showWishlistToast = (added) => {
    // Remove existing toast if any
    const existingToast = document.querySelector('.wishlist-toast');
    if (existingToast) {
      existingToast.remove();
    }

    const toast = document.createElement('div');
    toast.className = 'wishlist-toast';
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');

    // Heart icon
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('width', '18');
    svg.setAttribute('height', '18');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('fill', added ? 'currentColor' : 'none');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('stroke-width', '2');
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', 'M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z');
    svg.appendChild(path);
    toast.appendChild(svg);

    // Text
    const text = document.createElement('span');
    text.textContent = added
      ? themeString('wishlist.added', 'Added to wishlist')
      : themeString('wishlist.removed', 'Removed from wishlist');
    toast.appendChild(text);

    document.body.appendChild(toast);

    // Trigger animation
    requestAnimationFrame(() => {
      toast.classList.add('is-visible');
    });

    // Remove after 2.5s
    setTimeout(() => {
      toast.classList.remove('is-visible');
      setTimeout(() => toast.remove(), 300);
    }, 2500);
  };

  // Render wishlist page if grid element exists
  const renderWishlistPage = async () => {
    const grid = document.querySelector('[data-wishlist-grid]');
    if (!grid) return;

    const loadingEl = document.querySelector('[data-wishlist-loading]');
    const emptyEl = document.querySelector('[data-wishlist-empty]');
    const errorEl = document.querySelector('[data-wishlist-error]');
    const wishlist = getWishlist();

    if (errorEl) errorEl.hidden = true;

    // Show empty state if no items
    if (wishlist.length === 0) {
      if (loadingEl) loadingEl.hidden = true;
      if (emptyEl) emptyEl.hidden = false;
      if (errorEl) errorEl.hidden = true;
      grid.hidden = true;
      return;
    }

    // Fetch products and render
    try {
      const products = await Promise.all(
        wishlist.map(async (productId) => {
          try {
            const response = await fetch(routePath(`products/${productId}.js`));
            if (!response.ok) return null;
            return await response.json();
          } catch {
            return null;
          }
        })
      );

      // Filter out failed fetches and get valid handles
      const validProducts = products.filter((p) => p !== null);
      const validHandles = validProducts.map((p) => p.handle);

      // Clean up localStorage - remove handles that no longer exist
      if (validHandles.length !== wishlist.length) {
        saveWishlist(validHandles);
      }

      if (validProducts.length === 0) {
        if (loadingEl) loadingEl.hidden = true;
        if (emptyEl) emptyEl.hidden = false;
        if (errorEl) errorEl.hidden = true;
        grid.hidden = true;
        updateWishlistUI();
        return;
      }

      // Clear grid and render product cards using DOM methods
      grid.replaceChildren();
      validProducts.forEach((product) => {
        const card = createProductCard(product);
        grid.appendChild(card);
      });

      // Load cart module for wishlist-rendered cards
      await loadModule('cart');

      // Notify cart system to update steppers for wishlist cards
      document.dispatchEvent(new CustomEvent('cart:changed'));

      // Update wishlist button states and count after rendering
      updateWishlistUI();

      // Show grid, hide loading
      if (loadingEl) loadingEl.hidden = true;
      if (emptyEl) emptyEl.hidden = true;
      if (errorEl) errorEl.hidden = true;
      grid.hidden = false;
    } catch (error) {
      console.error('Failed to load wishlist products:', error);
      if (loadingEl) loadingEl.hidden = true;
      if (emptyEl) emptyEl.hidden = true;
      if (errorEl) errorEl.hidden = false;
      grid.hidden = true;
    }
  };

  // Create a product card element using safe DOM methods
  const createProductCard = (product) => {
    const sortedVariants = [...product.variants].sort((a, b) => parseFloat(a.price) - parseFloat(b.price));
    const cheapestAvailable = sortedVariants.find(v => v.available);
    const variant = cheapestAvailable || product.variants[0];
    const price = Number(variant.price);
    const comparePrice = variant.compare_at_price ? Number(variant.compare_at_price) : null;
    const onSale = comparePrice && comparePrice > price;
    const discountPercent = onSale ? Math.round(((comparePrice - price) / comparePrice) * 100) : 0;
    const rawImageUrl = typeof product.featured_image === 'string' ? product.featured_image : (product.image?.src || '');
    const imageUrl = rawImageUrl ? rawImageUrl.replace(/\.([^.]+)$/, '_400x.$1') : '';

    // Create article element
    const article = document.createElement('article');
    article.className = 'product-card product-card--fitmar';
    article.dataset.productId = product.id;
    article.dataset.productHandle = product.handle;

    // Media container
    const media = document.createElement('div');
    media.className = 'product-card__media';

    // Image link
    const imageLink = document.createElement('a');
    imageLink.href = routePath(`products/${product.handle}`);
    imageLink.className = 'product-card__media-link';
    imageLink.tabIndex = -1;
    imageLink.setAttribute('aria-hidden', 'true');

    if (imageUrl) {
      const img = document.createElement('img');
      img.src = imageUrl;
      img.alt = product.title;
      img.className = 'product-card__image product-card__image--primary';
      img.loading = 'lazy';
      img.width = 400;
      img.height = 400;
      imageLink.appendChild(img);
    } else {
      const placeholder = document.createElement('div');
      placeholder.className = 'product-card__placeholder';
      imageLink.appendChild(placeholder);
    }
    media.appendChild(imageLink);

    // Badges
    const badges = document.createElement('div');
    badges.className = 'product-card__badges';
    if (onSale) {
      const saleBadge = document.createElement('span');
      saleBadge.className = 'badge badge--sale';
      saleBadge.textContent = `-${discountPercent}%`;
      badges.appendChild(saleBadge);
    }
    media.appendChild(badges);

    // Wishlist button
    const wishlistDiv = document.createElement('div');
    wishlistDiv.className = 'product-card__wishlist';
    const wishlistBtn = document.createElement('button');
    wishlistBtn.className = 'product-card__wishlist-btn is-active';
    wishlistBtn.type = 'button';
    wishlistBtn.setAttribute('aria-label', `${themeString('products.removeFromFavorites')}: ${product.title}`);
    wishlistBtn.dataset.wishlistAdd = product.handle;
    // Heart SVG
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('width', '18');
    svg.setAttribute('height', '18');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('fill', 'currentColor');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('stroke-width', '2');
    svg.setAttribute('stroke-linecap', 'round');
    svg.setAttribute('stroke-linejoin', 'round');
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', 'M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z');
    svg.appendChild(path);
    wishlistBtn.appendChild(svg);
    wishlistDiv.appendChild(wishlistBtn);
    media.appendChild(wishlistDiv);

    // Cart action (add to cart + stepper)
    if (variant.available) {
      const cartAction = document.createElement('div');
      cartAction.className = 'product-card__cart-action';
      cartAction.dataset.cartAction = '';
      cartAction.dataset.variantId = variant.id;
      cartAction.dataset.productTitle = product.title;
      cartAction.dataset.variantTitle = variant.title || '';

      const addBtn = document.createElement('button');
      addBtn.type = 'button';
      addBtn.className = 'product-card__add-btn';
      addBtn.dataset.addToCart = '';
      addBtn.setAttribute('aria-label', `${themeString('products.addToCart')}: ${product.title}`);
      addBtn.textContent = themeString('products.addToCart');
      cartAction.appendChild(addBtn);

      const stepper = document.createElement('div');
      stepper.className = 'product-card__stepper';
      stepper.dataset.stepper = '';
      stepper.hidden = true;

      // Minus button
      const minusBtn = document.createElement('button');
      minusBtn.type = 'button';
      minusBtn.className = 'product-card__stepper-btn product-card__stepper-btn--minus';
      minusBtn.dataset.stepperMinus = '';
      minusBtn.setAttribute('aria-label', `${themeString('products.removeOne')}: ${product.title}`);
      const minusSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      minusSvg.setAttribute('width', '18');
      minusSvg.setAttribute('height', '18');
      minusSvg.setAttribute('viewBox', '0 0 24 24');
      minusSvg.setAttribute('fill', 'none');
      minusSvg.setAttribute('stroke', 'currentColor');
      minusSvg.setAttribute('stroke-width', '2');
      minusSvg.setAttribute('stroke-linecap', 'round');
      minusSvg.setAttribute('stroke-linejoin', 'round');
      const minusPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      minusPath.setAttribute('d', 'M20 12H4');
      minusSvg.appendChild(minusPath);
      minusBtn.appendChild(minusSvg);
      stepper.appendChild(minusBtn);

      // Qty display
      const qtySpan = document.createElement('span');
      qtySpan.className = 'product-card__stepper-qty';
      qtySpan.dataset.stepperQty = '';
      qtySpan.setAttribute('tabindex', '0');
      qtySpan.setAttribute('role', 'spinbutton');
      qtySpan.setAttribute('aria-valuenow', '0');
      qtySpan.setAttribute('aria-valuemin', '0');
      qtySpan.setAttribute('aria-label', `${themeString('products.quantity')}: ${product.title}`);
      qtySpan.textContent = '0';
      stepper.appendChild(qtySpan);

      // Plus button
      const plusBtn = document.createElement('button');
      plusBtn.type = 'button';
      plusBtn.className = 'product-card__stepper-btn product-card__stepper-btn--plus';
      plusBtn.dataset.stepperPlus = '';
      plusBtn.setAttribute('aria-label', `${themeString('products.addOne')}: ${product.title}`);
      const plusSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      plusSvg.setAttribute('width', '18');
      plusSvg.setAttribute('height', '18');
      plusSvg.setAttribute('viewBox', '0 0 24 24');
      plusSvg.setAttribute('fill', 'none');
      plusSvg.setAttribute('stroke', 'currentColor');
      plusSvg.setAttribute('stroke-width', '2');
      plusSvg.setAttribute('stroke-linecap', 'round');
      plusSvg.setAttribute('stroke-linejoin', 'round');
      const plusPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      plusPath.setAttribute('d', 'M12 4v16m8-8H4');
      plusSvg.appendChild(plusPath);
      plusBtn.appendChild(plusSvg);
      stepper.appendChild(plusBtn);

      cartAction.appendChild(stepper);
      media.appendChild(cartAction);
    }

    article.appendChild(media);

    // Content
    const content = document.createElement('div');
    content.className = 'product-card__content';

    // Title
    const titleH3 = document.createElement('h3');
    titleH3.className = 'product-card__title';
    const titleLink = document.createElement('a');
    titleLink.href = routePath(`products/${product.handle}`);
    titleLink.textContent = product.title;
    titleH3.appendChild(titleLink);
    content.appendChild(titleH3);

    // Footer with price
    const footer = document.createElement('div');
    footer.className = 'product-card__footer';
    const priceDiv = document.createElement('div');
    priceDiv.className = 'product-card__price';

    if (onSale) {
      const saleSpan = document.createElement('span');
      saleSpan.className = 'price__sale';
      saleSpan.textContent = formatStoreMoneyFromMinorUnits(price);
      priceDiv.appendChild(saleSpan);

      const compareSpan = document.createElement('span');
      compareSpan.className = 'price__compare';
      compareSpan.textContent = formatStoreMoneyFromMinorUnits(comparePrice);
      priceDiv.appendChild(compareSpan);
    } else {
      const regularSpan = document.createElement('span');
      regularSpan.className = 'price__regular';
      regularSpan.textContent = formatStoreMoneyFromMinorUnits(price);
      priceDiv.appendChild(regularSpan);
    }

    footer.appendChild(priceDiv);
    content.appendChild(footer);
    article.appendChild(content);

    return article;
  };

  // Handle wishlist removal on wishlist page with animation
  const handleWishlistPageRemoval = (productId) => {
    const grid = document.querySelector('[data-wishlist-grid]');
    if (!grid) return;

    const card = grid.querySelector(`[data-product-handle="${productId}"]`);
    if (card) {
      card.classList.add('is-removing');
      setTimeout(() => {
        card.remove();
        // Check if grid is now empty
        if (grid.children.length === 0) {
          const emptyEl = document.querySelector('[data-wishlist-empty]');
          if (emptyEl) emptyEl.hidden = false;
          grid.hidden = true;
        }
      }, 300);
    }
  };

  // Event delegation for wishlist buttons
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-wishlist-add]');
    if (!btn) return;

    e.preventDefault();
    const productId = btn.dataset.wishlistAdd;
    const wasInWishlist = isInWishlist(productId);
    const wasAdded = toggleWishlist(productId);

    // Show toast notification
    showWishlistToast(wasAdded);

    // If on wishlist page and item was removed, animate removal
    if (wasInWishlist && document.querySelector('[data-wishlist-grid]')) {
      handleWishlistPageRemoval(productId);
    }
  });

  // Initialize UI on load
  updateWishlistUI();

  // Render wishlist page if present
  renderWishlistPage();
};

/**
 * Filter Drawer (Mobile)
 */
const initFilterDrawer = () => {
  const drawer = document.querySelector('[data-filter-drawer]');
  const closeButtons = document.querySelectorAll('[data-filter-close]');
  const backdrop = document.querySelector('[data-filter-backdrop]');

  if (!drawer) return;

  // AbortController for cancelling in-flight filter requests
  let filterAbortController = null;
  let activeFilterTrigger = null;
  let previousBodyOverflow = '';

  drawer.setAttribute('aria-hidden', 'true');
  drawer.inert = true;

  const openDrawer = (trigger = null) => {
    activeFilterTrigger = trigger || document.activeElement;
    previousBodyOverflow = document.body.style.overflow;
    drawer.inert = false;
    drawer.setAttribute('aria-hidden', 'false');
    drawer.classList.add('is-open');
    backdrop?.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    activeFilterTrigger?.setAttribute?.('aria-expanded', 'true');
    // Focus first close button for accessibility
    closeButtons[0]?.focus({ preventScroll: true });
  };

  const closeDrawer = () => {
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    drawer.inert = true;
    backdrop?.classList.remove('is-open');
    document.body.style.overflow = previousBodyOverflow;
    activeFilterTrigger?.setAttribute?.('aria-expanded', 'false');
    if (activeFilterTrigger?.isConnected) {
      activeFilterTrigger.focus({ preventScroll: true });
    }
    activeFilterTrigger = null;
  };

  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-filter-toggle]');
    if (trigger) {
      openDrawer(trigger);
    }
  });
  closeButtons.forEach((btn) => btn.addEventListener('click', closeDrawer));
  backdrop?.addEventListener('click', closeDrawer);

  document.addEventListener('keydown', (e) => {
    if (!drawer.classList.contains('is-open')) return;

    if (e.key === 'Escape') {
      closeDrawer();
      return;
    }

    trapFocus(e, drawer, closeButtons[0] || drawer);
  });

  // AJAX filtering
  const sectionId = document.querySelector('[data-section-id]')?.dataset.sectionId;
  const mainContent = document.querySelector('[data-collection-main]');

  if (!sectionId || !mainContent) return;

  // Build filter URL from all checked inputs
  // explicitChange: optional override for a specific input's checked state
  const buildFilterUrl = (explicitChange = null) => {
    const params = new URLSearchParams();

    document.querySelectorAll('[data-sidebar-filters] [data-filter-input]').forEach((input) => {
      let isChecked = input.checked;

      // For the just-changed input, use explicit state from event
      if (explicitChange &&
          input.name === explicitChange.name &&
          input.value === explicitChange.value) {
        isChecked = explicitChange.checked;
      }

      if (isChecked) {
        params.append(input.name, input.value);
      }
    });

    const selectedSort = document.querySelector('[data-sort-options] [aria-selected="true"]')?.dataset.sortValue ||
      new URLSearchParams(window.location.search).get('sort_by');
    if (selectedSort) {
      params.set('sort_by', selectedSort);
    }
    return params.toString();
  };

  // Fetch and update collection via AJAX
  // Note: Content from Shopify Section Rendering API is trusted same-origin content
  const updateCollection = async (url, pushState = true) => {
    // Cancel any in-flight request
    if (filterAbortController) {
      filterAbortController.abort();
    }
    filterAbortController = new AbortController();

    mainContent.classList.add('is-loading');
    mainContent.setAttribute('aria-busy', 'true');

    try {
      const fetchUrl = new URL(url, window.location.origin);
      fetchUrl.searchParams.set('sections', 'main-collection');
      const response = await fetch(fetchUrl.toString(), {
        signal: filterAbortController.signal
      });
      if (!response.ok) throw new Error('Network response was not ok');

      const data = await response.json();
      const html = data['main-collection'];

      // Parse the new HTML from trusted Shopify sections API
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Update product grid using replaceWith (safe DOM replacement)
      const newGrid = doc.querySelector('[data-product-grid]');
      const currentGrid = document.querySelector('[data-product-grid]');
      if (newGrid && currentGrid) {
        currentGrid.replaceWith(newGrid);
      }

      // Update product count text content safely
      const newCount = doc.querySelector('[data-product-count]');
      const currentCount = document.querySelector('[data-product-count]');
      if (newCount && currentCount) {
        currentCount.textContent = newCount.textContent;
      }

      // Update sidebar filters using replaceWith (trusted Shopify content)
      const newSidebar = doc.querySelector('[data-sidebar-filters]');
      const currentSidebar = document.querySelector('[data-sidebar-filters]');
      if (newSidebar && currentSidebar) {
        currentSidebar.replaceWith(newSidebar);
      }

      // Update mobile drawer filters
      const newDrawerFilters = doc.querySelector('[data-filter-drawer] .sidebar-filters');
      const currentDrawerFilters = document.querySelector('[data-filter-drawer] .sidebar-filters');
      if (newDrawerFilters && currentDrawerFilters) {
        currentDrawerFilters.replaceWith(newDrawerFilters);
      }

      // Update filter toggle badge
      const newToggle = doc.querySelector('[data-filter-toggle]');
      const currentToggle = document.querySelector('[data-filter-toggle]');
      if (newToggle && currentToggle) {
        currentToggle.replaceWith(newToggle);
      }

      // Update active filter pills
      const newActiveFilters = doc.querySelector('.main-collection__active-filters-wrapper');
      const currentActiveFilters = document.querySelector('.main-collection__active-filters-wrapper');
      const toolbar = document.querySelector('.main-collection__toolbar');
      if (newActiveFilters && currentActiveFilters) {
        currentActiveFilters.replaceWith(newActiveFilters);
      } else if (newActiveFilters && !currentActiveFilters) {
        toolbar?.after(newActiveFilters);
      } else if (!newActiveFilters && currentActiveFilters) {
        currentActiveFilters.remove();
      }

      // Update pagination
      const newPagination = doc.querySelector('.pagination');
      const currentPagination = document.querySelector('.pagination');
      if (newPagination) {
        if (currentPagination) {
          currentPagination.replaceWith(newPagination);
        } else {
          document.querySelector('[data-product-grid]')?.after(newPagination);
        }
      } else if (currentPagination) {
        currentPagination.remove();
      }

      // Update empty state
      const newEmpty = doc.querySelector('.empty-state');
      const currentEmpty = document.querySelector('.empty-state');
      const productGrid = document.querySelector('[data-product-grid]');
      if (newEmpty && !currentEmpty) {
        productGrid?.replaceWith(newEmpty);
      } else if (!newEmpty && currentEmpty) {
        const newGridFromDoc = doc.querySelector('[data-product-grid]');
        if (newGridFromDoc) currentEmpty.replaceWith(newGridFromDoc);
      }

      // Update URL
      if (pushState) {
        history.pushState({ filterUrl: url }, '', url);
      }
    } catch (error) {
      // Ignore aborted requests
      if (error.name === 'AbortError') {
        return;
      }
      console.error('Filter update failed:', error);
      // Fallback to full page reload
      window.location.href = url;
    } finally {
      mainContent.classList.remove('is-loading');
      mainContent.setAttribute('aria-busy', 'false');
    }
  };

  // Handle filter checkbox changes with event delegation
  document.addEventListener('change', (e) => {
    const input = e.target.closest('[data-filter-input]');
    if (!input) return;

    // Capture explicit state from event target to avoid race condition
    const clickedInput = {
      name: input.name,
      value: input.value,
      checked: input.checked
    };

    const params = buildFilterUrl(clickedInput);
    const url = `${window.location.pathname}${params ? '?' + params : ''}`;
    updateCollection(url);
  });

  // Custom Sort Dropdown
  const sortDropdown = document.querySelector('[data-sort-dropdown]');
  if (sortDropdown) {
    const trigger = sortDropdown.querySelector('[data-sort-trigger]');
    const options = sortDropdown.querySelector('[data-sort-options]');
    const label = sortDropdown.querySelector('[data-sort-label]');

    // Toggle dropdown
    trigger?.addEventListener('click', () => {
      const isOpen = trigger.getAttribute('aria-expanded') === 'true';
      trigger.setAttribute('aria-expanded', !isOpen);
      options.hidden = isOpen;

      if (!isOpen) {
        // Focus the selected option or first option
        const selected = options.querySelector('.is-selected') || options.querySelector('[data-sort-value]');
        selected?.focus();
      }
    });

    // Close on click outside
    document.addEventListener('click', (e) => {
      if (!sortDropdown.contains(e.target)) {
        trigger?.setAttribute('aria-expanded', 'false');
        if (options) options.hidden = true;
      }
    });

    // Close on Escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && options && !options.hidden) {
        trigger?.setAttribute('aria-expanded', 'false');
        options.hidden = true;
        trigger?.focus();
      }
    });

    // Keyboard navigation within dropdown
    options?.addEventListener('keydown', (e) => {
      const items = [...options.querySelectorAll('[data-sort-value]')];
      const currentIndex = items.indexOf(document.activeElement);

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const nextIndex = currentIndex < items.length - 1 ? currentIndex + 1 : 0;
        items[nextIndex]?.focus();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        const prevIndex = currentIndex > 0 ? currentIndex - 1 : items.length - 1;
        items[prevIndex]?.focus();
      }
    });

    // Handle option selection
    options?.addEventListener('click', (e) => {
      const optionBtn = e.target.closest('[data-sort-value]');
      if (!optionBtn) return;

      const value = optionBtn.dataset.sortValue;

      // Update label
      if (label) label.textContent = optionBtn.textContent.trim();

      // Update selected state
      options.querySelectorAll('[data-sort-value]').forEach((btn) => {
        btn.classList.remove('is-selected');
        btn.setAttribute('aria-selected', 'false');
      });
      optionBtn.classList.add('is-selected');
      optionBtn.setAttribute('aria-selected', 'true');

      // Close dropdown
      trigger?.setAttribute('aria-expanded', 'false');
      options.hidden = true;
      trigger?.focus({ preventScroll: true });

      // Update collection with new sort
      const params = new URLSearchParams();
      document.querySelectorAll('[data-filter-input]:checked').forEach((input) => {
        params.append(input.name, input.value);
      });
      if (value) {
        params.set('sort_by', value);
      }
      const url = `${window.location.pathname}${params.toString() ? '?' + params.toString() : ''}`;
      updateCollection(url);
    });
  }

  // Handle browser back/forward
  window.addEventListener('popstate', (e) => {
    if (e.state?.filterUrl) {
      updateCollection(e.state.filterUrl, false);
    } else {
      updateCollection(window.location.href, false);
    }
  });

  // Store initial state
  history.replaceState({ filterUrl: window.location.href }, '', window.location.href);
};

/**
 * Dropdown Navigation
 */
const initDropdownNav = () => {
  const isHoverDevice = window.matchMedia('(hover: hover)').matches;

  document.querySelectorAll('.header__nav-item--dropdown, .header-category-bar__item--dropdown').forEach((item) => {
    const trigger = item.querySelector('button, a[aria-haspopup]');
    const dropdown = item.querySelector('.header__dropdown, .header-category-bar__dropdown');

    if (!trigger || !dropdown) return;

    // Remove hidden attribute — CSS handles visibility now
    dropdown.removeAttribute('hidden');

    if (isHoverDevice) {
      // Hover device: CSS handles show/hide, JS manages aria
      item.addEventListener('mouseenter', () => {
        trigger.setAttribute('aria-expanded', 'true');
        dropdown.classList.add('is-open');
      });

      item.addEventListener('mouseleave', () => {
        trigger.setAttribute('aria-expanded', 'false');
        dropdown.classList.remove('is-open');
      });

      // Also support keyboard focus
      item.addEventListener('focusin', () => {
        trigger.setAttribute('aria-expanded', 'true');
        dropdown.classList.add('is-open');
      });

      item.addEventListener('focusout', (e) => {
        if (!item.contains(e.relatedTarget)) {
          trigger.setAttribute('aria-expanded', 'false');
          dropdown.classList.remove('is-open');
        }
      });
    } else {
      // Touch device: click-based toggle (only for button triggers, not links)
      if (trigger.tagName === 'BUTTON') {
        trigger.addEventListener('click', () => {
          const isOpen = dropdown.classList.contains('is-open');
          trigger.setAttribute('aria-expanded', !isOpen);
          dropdown.classList.toggle('is-open');
        });
      }

      // Close on outside click
      document.addEventListener('click', (e) => {
        if (!item.contains(e.target)) {
          trigger.setAttribute('aria-expanded', 'false');
          dropdown.classList.remove('is-open');
        }
      });
    }

    // Close on escape (both modes)
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && dropdown.classList.contains('is-open')) {
        trigger.setAttribute('aria-expanded', 'false');
        dropdown.classList.remove('is-open');
        trigger.focus();
      }
    });
  });
};

/**
 * Country Selector Modal
 */
const initCountrySelector = () => {
  const toggles = document.querySelectorAll('[data-country-selector-toggle]');
  const modal = document.querySelector('[data-country-modal]');
  const dialog = modal?.querySelector('.country-modal__dialog');
  const backdrop = document.querySelector('[data-country-modal-backdrop]');
  const closeBtn = document.querySelector('[data-country-modal-close]');

  if (!toggles.length || !modal) return;

  let activeToggle = null;
  let previousBodyOverflow = '';

  const openModal = (toggle) => {
    activeToggle = toggle;
    previousBodyOverflow = document.body.style.overflow;
    modal.hidden = false;
    modal.setAttribute('aria-hidden', 'false');
    toggles.forEach((t) => t.setAttribute('aria-expanded', 'true'));
    document.body.style.overflow = 'hidden';
    (dialog || closeBtn)?.focus({ preventScroll: true });
  };

  const closeModal = () => {
    if (modal.hidden) return;

    modal.hidden = true;
    modal.setAttribute('aria-hidden', 'true');
    toggles.forEach((t) => t.setAttribute('aria-expanded', 'false'));
    document.body.style.overflow = previousBodyOverflow;
    activeToggle?.focus({ preventScroll: true });
    activeToggle = null;
  };

  toggles.forEach((toggle) => {
    toggle.addEventListener('click', () => openModal(toggle));
  });
  backdrop?.addEventListener('click', closeModal);
  closeBtn?.addEventListener('click', closeModal);

  document.addEventListener('keydown', (e) => {
    if (modal.hidden) return;

    if (e.key === 'Escape') {
      closeModal();
      return;
    }

    trapFocus(e, dialog || modal, closeBtn || dialog || modal);
  });
};

/**
 * Footer Money-Back Guarantee Modal
 */
const initFooterGuaranteeModal = () => {
  const openButtons = document.querySelectorAll('[data-footer-guarantee-open]');
  const modal = document.querySelector('[data-footer-guarantee-modal]');
  const dialog = modal?.querySelector('.footer-guarantee-modal__dialog');
  const closeButtons = modal?.querySelectorAll('[data-footer-guarantee-close]');

  if (!openButtons.length || !modal || !dialog || !closeButtons?.length) return;

  let activeTrigger = null;
  let previousBodyOverflow = '';

  const openModal = (trigger) => {
    activeTrigger = trigger;
    previousBodyOverflow = document.body.style.overflow;
    modal.hidden = false;
    modal.setAttribute('aria-hidden', 'false');
    openButtons.forEach((button) => button.setAttribute('aria-expanded', 'true'));
    document.body.style.overflow = 'hidden';
    dialog.focus({ preventScroll: true });
  };

  const closeModal = () => {
    if (modal.hidden) return;

    modal.hidden = true;
    modal.setAttribute('aria-hidden', 'true');
    openButtons.forEach((button) => button.setAttribute('aria-expanded', 'false'));
    document.body.style.overflow = previousBodyOverflow;

    if (activeTrigger?.isConnected) {
      activeTrigger.focus({ preventScroll: true });
    }

    activeTrigger = null;
  };

  openButtons.forEach((button) => {
    button.addEventListener('click', () => openModal(button));
  });

  closeButtons.forEach((button) => {
    button.addEventListener('click', closeModal);
  });

  document.addEventListener('keydown', (event) => {
    if (modal.hidden) return;

    if (event.key === 'Escape') {
      closeModal();
      return;
    }

    trapFocus(event, dialog, dialog);
  });
};

/**
 * Smart Header - Category Nav Hide/Show on Scroll
 * Desktop only (990px+): hides category nav on scroll down, shows on scroll up
 */
const initSmartHeader = () => {
  const categoryNav = document.querySelector('[data-category-nav]');
  if (!categoryNav) return;

  const SCROLL_THRESHOLD = 10;
  const DESKTOP_BREAKPOINT = 990;

  let lastScrollY = window.scrollY;
  let ticking = false;
  let isHidden = false;

  const isDesktop = () => window.innerWidth >= DESKTOP_BREAKPOINT;

  const updateNavVisibility = () => {
    const currentScrollY = window.scrollY;
    const scrollDelta = currentScrollY - lastScrollY;

    // Only apply on desktop
    if (!isDesktop()) {
      categoryNav.classList.remove('is-hidden');
      isHidden = false;
      lastScrollY = currentScrollY;
      ticking = false;
      return;
    }

    // At top of page - always show
    if (currentScrollY <= 0) {
      categoryNav.classList.remove('is-hidden');
      isHidden = false;
      lastScrollY = currentScrollY;
      ticking = false;
      return;
    }

    // Apply threshold to prevent jitter
    if (Math.abs(scrollDelta) < SCROLL_THRESHOLD) {
      ticking = false;
      return;
    }

    // Scroll down - hide
    if (scrollDelta > 0 && !isHidden) {
      categoryNav.classList.add('is-hidden');
      isHidden = true;
    }
    // Scroll up - show
    else if (scrollDelta < 0 && isHidden) {
      categoryNav.classList.remove('is-hidden');
      isHidden = false;
    }

    lastScrollY = currentScrollY;
    ticking = false;
  };

  const onScroll = () => {
    if (!ticking) {
      requestAnimationFrame(updateNavVisibility);
      ticking = true;
    }
  };

  const onResize = () => {
    if (!isDesktop()) {
      categoryNav.classList.remove('is-hidden');
      isHidden = false;
    }
  };

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onResize, { passive: true });
};

/**
 * Carousel Navigation (optional enhancement)
 */
export const initCarousel = (carousel) => {
  const track = carousel.querySelector('.carousel__track');
  const prevBtn = carousel.querySelector('[data-carousel-prev]');
  const nextBtn = carousel.querySelector('[data-carousel-next]');
  const dots = carousel.querySelectorAll('.carousel__dot');

  if (!track) return;

  const scrollToSlide = (index) => {
    const slides = track.children;
    if (slides[index]) {
      slides[index].scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'start',
      });
    }
  };

  // Navigation buttons
  prevBtn?.addEventListener('click', () => {
    track.scrollBy({ left: -track.offsetWidth, behavior: 'smooth' });
  });

  nextBtn?.addEventListener('click', () => {
    track.scrollBy({ left: track.offsetWidth, behavior: 'smooth' });
  });

  // Dot navigation
  dots.forEach((dot, index) => {
    dot.addEventListener('click', () => scrollToSlide(index));
  });

  // Update active dot on scroll
  if (dots.length > 0) {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = [...track.children].indexOf(entry.target);
            dots.forEach((dot, i) => {
              dot.classList.toggle('is-active', i === index);
            });
          }
        });
      },
      { root: track, threshold: 0.5 }
    );

    [...track.children].forEach((slide) => observer.observe(slide));

    // Cleanup observer on page navigation to prevent memory leaks
    window.addEventListener('pagehide', () => observer.disconnect(), { once: true });
  }
};

// Initialize carousels
document.querySelectorAll('.carousel').forEach(initCarousel);

// Initialize hero carousels (infinite loop)
document.querySelectorAll('.hero-carousel__wrapper').forEach((wrapper) => {
  const track = wrapper.querySelector('.hero-carousel__track');
  const prevBtn = wrapper.querySelector('[data-carousel-prev]');
  const nextBtn = wrapper.querySelector('[data-carousel-next]');

  if (!track) return;

  const realCards = Array.from(track.querySelectorAll('.hero-carousel__card'));
  if (realCards.length < 2) return; // No loop needed for 0-1 slides

  // Skip cloning in Shopify theme editor (editor needs stable DOM)
  if (window.Shopify && window.Shopify.designMode) {
    // Fallback: simple scroll without loop
    const getScrollAmount = () => {
      const card = track.querySelector('.hero-carousel__card');
      const gap = 11.2;
      return card ? (card.offsetWidth + gap) * 3 : track.offsetWidth;
    };
    prevBtn?.addEventListener('click', () => {
      track.scrollBy({ left: -getScrollAmount(), behavior: 'smooth' });
    });
    nextBtn?.addEventListener('click', () => {
      track.scrollBy({ left: getScrollAmount(), behavior: 'smooth' });
    });
    return;
  }

  // --- Phase 1: Clone slides ---
  const cloneCard = (card) => {
    const clone = card.cloneNode(true);
    clone.setAttribute('data-clone', '');
    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('data-shopify-editor-block');
    // Make links non-focusable in clones
    clone.querySelectorAll('a, button').forEach((el) => el.setAttribute('tabindex', '-1'));
    // Optimize clone images: lazy load, remove fetchpriority
    clone.querySelectorAll('img').forEach((img) => {
      img.removeAttribute('fetchpriority');
      img.setAttribute('loading', 'lazy');
    });
    return clone;
  };

  // Append clones after last real card, prepend clones before first
  const prependClones = realCards.map(cloneCard);
  const appendClones = realCards.map(cloneCard);

  // Insert prepend clones before first real card
  const firstReal = realCards[0];
  prependClones.forEach((clone) => track.insertBefore(clone, firstReal));

  // Append clones after last real card (before the ::after pseudo-element spacer)
  appendClones.forEach((clone) => track.appendChild(clone));

  // --- Phase 2: Initial position ---
  const edgeInset = parseFloat(getComputedStyle(track).scrollPaddingInlineStart) || 0;

  const scrollToFirstReal = () => {
    track.classList.add('hero-carousel__track--resetting');
    track.scrollLeft = firstReal.offsetLeft - edgeInset;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        track.classList.remove('hero-carousel__track--resetting');
      });
    });
  };

  scrollToFirstReal();

  // --- Phase 3: Boundary detection ---
  let isResetting = false;
  let scrollTimeout = null;
  let rafId = null;

  const performJump = () => {
    if (isResetting) return;
    isResetting = true;

    const currentScroll = track.scrollLeft;

    // Find first & last real card positions
    const firstRealLeft = firstReal.offsetLeft - edgeInset;
    const lastReal = realCards[realCards.length - 1];
    const lastRealLeft = lastReal.offsetLeft - edgeInset;

    // Prepend zone: if we scrolled into the prepend clones
    const prependBoundary = firstRealLeft - 10;
    // Append zone: if we scrolled past the last real card
    const lastRealRight = lastReal.offsetLeft + lastReal.offsetWidth;
    const appendBoundary = lastRealRight;

    if (currentScroll < prependBoundary) {
      // We're in the prepend clone zone — jump to corresponding real position
      // The prepend clones mirror the real cards, so offset from prepend start = offset from real start
      const prependStart = prependClones[0].offsetLeft - edgeInset;
      const offset = currentScroll - prependStart;
      // Corresponding position in real zone (same offset from last real card area)
      const realStart = realCards[0].offsetLeft - edgeInset;
      const realEnd = lastReal.offsetLeft - edgeInset;
      const totalRealWidth = realEnd - realStart + lastReal.offsetWidth;
      const newScroll = realStart + totalRealWidth + offset - (realEnd - realStart);

      track.classList.add('hero-carousel__track--resetting');
      // Map: we were at prepend clone index → jump to same index in real cards but from the end
      // Simpler approach: find which clone we're closest to, jump to corresponding real card
      let closestCloneIdx = 0;
      let closestDist = Infinity;
      prependClones.forEach((clone, i) => {
        const dist = Math.abs(clone.offsetLeft - edgeInset - currentScroll);
        if (dist < closestDist) {
          closestDist = dist;
          closestCloneIdx = i;
        }
      });
      track.scrollLeft = realCards[closestCloneIdx].offsetLeft - edgeInset;
      rafId = requestAnimationFrame(() => {
        rafId = requestAnimationFrame(() => {
          track.classList.remove('hero-carousel__track--resetting');
          isResetting = false;
        });
      });
    } else if (currentScroll >= appendBoundary) {
      // We're in the append clone zone — jump to corresponding real card
      track.classList.add('hero-carousel__track--resetting');
      let closestCloneIdx = 0;
      let closestDist = Infinity;
      appendClones.forEach((clone, i) => {
        const dist = Math.abs(clone.offsetLeft - edgeInset - currentScroll);
        if (dist < closestDist) {
          closestDist = dist;
          closestCloneIdx = i;
        }
      });
      track.scrollLeft = realCards[closestCloneIdx].offsetLeft - edgeInset;
      rafId = requestAnimationFrame(() => {
        rafId = requestAnimationFrame(() => {
          track.classList.remove('hero-carousel__track--resetting');
          isResetting = false;
        });
      });
    } else {
      isResetting = false;
    }
  };

  const onScroll = () => {
    if (isResetting) return;
    clearTimeout(scrollTimeout);
    scrollTimeout = setTimeout(performJump, 100);
  };

  track.addEventListener('scroll', onScroll, { passive: true });

  // --- Phase 4: Arrow buttons ---
  const getScrollAmount = () => {
    const card = track.querySelector('.hero-carousel__card');
    const gap = 11.2; // 0.7rem ≈ 11.2px
    return card ? (card.offsetWidth + gap) * 3 : track.offsetWidth;
  };

  prevBtn?.addEventListener('click', () => {
    track.scrollBy({ left: -getScrollAmount(), behavior: 'smooth' });
  });

  nextBtn?.addEventListener('click', () => {
    track.scrollBy({ left: getScrollAmount(), behavior: 'smooth' });
  });

  // --- Phase 5: Resize handler ---
  let resizeRaf = null;
  const onResize = () => {
    if (resizeRaf) cancelAnimationFrame(resizeRaf);
    resizeRaf = requestAnimationFrame(scrollToFirstReal);
  };
  window.addEventListener('resize', onResize, { passive: true });

  // --- Phase 6: Cleanup ---
  window.addEventListener('pagehide', () => {
    clearTimeout(scrollTimeout);
    if (rafId) cancelAnimationFrame(rafId);
    if (resizeRaf) cancelAnimationFrame(resizeRaf);
    track.removeEventListener('scroll', onScroll);
    window.removeEventListener('resize', onResize);
  }, { once: true });
});

// Run on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

// Export for use by other modules
export { loadModule };
